import PropTypes from "prop-types";
import {isString} from "./javascript.js";

/**
 * Fetches a string from the object tree in localizationData given its keys. The last parameter can be an object containing replacement strings.
 * @param {Object} localizationData
 * @param {...(string|Object.<string, string>)} keys
 * @example localizationData := {mainMenu: {myString: "Test {in}."}}, localize("mainMenu", "myString", {in: "out"} -> "Test out."
 * @return {string}
 */
export function localize(localizationData, ...keys) {
    let node = localizationData;
    if (node) {
        for (const idx in keys) {
            const key = keys[idx];
            if (isString(node) && !isString(key)) {
                Object.entries(key).forEach(r => {
                    node = node.replace(RegExp(`\\{${r[0]}\\}`, "g"), r[1]);
                });
            } else {
                node = node[key];
                if (!node) {
                    break;
                }
            }
        }
    }

    return node || `Unknown localization path ${keys}.`;
}

/**
 * Klass som hanterar språkberoende operationer
 */
export class Localization {
    constructor(currentLanguage, localizationData, systemInfo){
        this.currentLanguage = currentLanguage;
        this.localizationData = Object.keys(localizationData).length > 0 ? localizationData : undefined;
        this.text = this.text.bind(this);
        this.systemInfo = systemInfo;
    }

    /**
     * Översätter texter
     * @param {*} keys 
     * @returns {string}
     */
    text(...keys) {
        if (!this.localizationData){
            return "";
        }
        return localize(this.localizationData, ...keys);
    }
}

Localization.propTypes = PropTypes.shape({
    text: PropTypes.func.isRequired,
    systemInfo: PropTypes.object.isRequired
});

let currentLocalization = undefined;


/**
 * Returnerar memoriserad klassinstans givet localiseringsdata i state. Samma instans returnerar varje gång tills dess att språk byts
 * @param {Object} state 
 * @returns {Class}
 */
export function createLocalization(state){
    if (currentLocalization === undefined || currentLocalization.currentLanguage !== state.authentication.languageId || currentLocalization.localizationData !== state.localization.data || currentLocalization.systemInfo !== state.userSettings.systemInfo){
        currentLocalization = new Localization(state.authentication.languageId, state.localization.data, state.userSettings.systemInfo);
    }
    return currentLocalization;
}

/**
 * Given the current localization data in the state, creates a singleton function which is only updated when the data changes and which maps to the localize function without
 * also manually passing the localization data object.
 * 
 * @param {Object} state 
 * @returns {string}
 */
export function createLocalizeFunction(state) {
    return createLocalization(state).text;
}

export const toLocaleFixed = (val, noOfdecimals) => val != undefined ? val.toLocaleString("nb-NO", { minimumFractionDigits:noOfdecimals, maximumFractionDigits:noOfdecimals }): null;


/*

    createLocalizeFunction => getLocalization(state)
    returnerar en instans av en klass. Samma instans returneras varje gång.
    Medlemmar:
        text()  => översätter en text
        codeValueChoiseList(cn) => extraherar


*/